"use client";

import debounce from "lodash.debounce";
import { Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

interface StockNumberInputProps {
  label?: string;
  onValueChange: (value: string) => void;
  onReset: () => void;
  value?: string | null;
  className?: string;
  placeholder?: string;
  isDisabled?: boolean;
  noDebounce?: boolean;
  isLoading?: boolean;
}

const StockNumberInput: React.FC<StockNumberInputProps> = ({
  label,
  onValueChange,
  onReset,
  value,
  className = "",
  placeholder,
  isDisabled,
  isLoading = false,
  noDebounce = false,
}) => {
  const t = useTranslations("Filters");
  const [stockNumber, setStockNumber] = useState(value ?? "");
  // Tracks the value we last emitted so we can distinguish a genuine external
  // change from a late-arriving echo of our own input (which must not override
  // newer keystrokes still sitting in local state).
  const lastEmittedValue = useRef(value ?? "");

  useEffect(() => {
    const incoming = value ?? "";
    if (incoming === lastEmittedValue.current) return;
    lastEmittedValue.current = incoming;
    setStockNumber(incoming);
  }, [value]);

  const debouncedHandler = useMemo(
    () => ({
      change: debounce(
        (value: string) => {
          lastEmittedValue.current = value;
          onValueChange?.(value);
        },
        noDebounce ? 0 : 400,
      ),
    }),
    [onValueChange, noDebounce],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStockNumber(e.target.value);
    debouncedHandler.change(e.target.value);
  };

  const handleReset = () => {
    debouncedHandler.change.cancel();
    lastEmittedValue.current = "";
    setStockNumber("");
    onValueChange?.("");
    onReset?.();
  };

  useEffect(() => {
    return () => {
      debouncedHandler.change.cancel();
    };
  }, [debouncedHandler]);

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <label htmlFor="stockNumber" className="inter text-sm font-semibold text-[#000000]">
          {label ?? t("stockNo")}
        </label>
        <button
          onClick={handleReset}
          className="inter text-sm text-secondary-foreground transition-colors hover:cursor-pointer hover:text-primary"
        >
          {t("reset")}
        </button>
      </div>
      <div className="relative">
        <input
          name="stockNumber"
          className="!focus:border-grey-border inter placeholder:inter mt-2 w-full rounded-2xl border border-[#EEF1F6] bg-secondary-light p-5 py-4 placeholder:text-base hover:border-grey-border focus:outline-none focus:ring-1 focus:ring-grey-border"
          placeholder={placeholder ?? t("stockNo")}
          value={stockNumber}
          onChange={handleChange}
          disabled={isDisabled}
        />
        {isLoading && (
          <div className="absolute right-4 top-7">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
      </div>
    </div>
  );
};

export default StockNumberInput;
